import React from 'react';

/**
 * Drop-in replacement for React.lazy that survives a stale dynamic-import failure.
 *
 * Two situations produce "Failed to fetch dynamically imported module":
 *  - dev: a Vite dependency re-optimization fires while a lazy chunk is in flight,
 *    invalidating the module graph mid-request;
 *  - prod: after a deploy, a client holding the old index.html requests hashed
 *    chunk filenames the new build no longer serves.
 *
 * In both cases a single full reload pulls the fresh module graph. We reload at
 * most once per session (guarded by sessionStorage) so a genuinely broken import
 * surfaces its real error instead of looping.
 */
const RELOAD_FLAG = 'lazyWithRetry-reloaded';

const lazyWithRetry = importer =>
  React.lazy(() =>
    importer()
      .then(module => {
        sessionStorage.removeItem(RELOAD_FLAG);
        return module;
      })
      .catch(error => {
        if (!sessionStorage.getItem(RELOAD_FLAG)) {
          sessionStorage.setItem(RELOAD_FLAG, '1');
          window.location.reload();
          // Halt rendering until the reload takes over.
          return new Promise(() => {});
        }
        throw error;
      }),
  );

export default lazyWithRetry;
